import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Headphones, Layers, FileCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Headphones className="w-12 h-12" />,
      title: 'Customer Service Representatives (CSR)',
      description: 'Dedicated professionals who manage customer communications, order intake, quotation requests, production updates, email management, and workflow coordination.',
      image: 'https://images.unsplash.com/photo-1626863905121-3b0c0ed7b94c',
      features: [
        'Customer communications management',
        'Order intake and processing',
        'Quotation request handling',
        'Production status updates',
        'Email management',
        'Workflow coordination',
        'Account support'
      ]
    },
    {
      icon: <Layers className="w-12 h-12" />,
      title: 'Graphic Production Specialists',
      description: 'Professionals trained in artwork review, file preparation, print-ready verification, prepress support, and production documentation.',
      image: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e',
      features: [
        'Artwork review and verification',
        'File preparation for production',
        'Print-ready file verification',
        'Basic prepress support',
        'Production documentation',
        'PDF review and checking',
        'Resolution and bleed verification'
      ]
    },
    {
      icon: <FileCheck className="w-12 h-12" />,
      title: 'Order Processing & File Management',
      description: 'Support for receiving files, organizing customer assets, generating production orders, tracking projects, and maintaining workflow accuracy.',
      image: 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a',
      features: [
        'File reception and organization',
        'Customer asset management',
        'Production order generation',
        'Project tracking',
        'Workflow accuracy maintenance',
        'Job ticket creation',
        'Production scheduling support'
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="page-hero py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Specialized <span className="text-gradient">Print Industry Support</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto">
            Dedicated professionals who understand print production environments and graphic arts processes
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={index}
                className={`service-item grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="icon-wrapper w-20 h-20 rounded-2xl bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white">
                    {service.icon}
                  </div>
                  <h2 className="text-4xl font-bold mb-6">{service.title}</h2>
                  <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-4 mb-8">
                    <h3 className="text-2xl font-semibold mb-4">Key Responsibilities:</h3>
                    <ul className="space-y-3">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-lg text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to="/contact">
                    <Button size="lg" className="btn-primary group">
                      Request Consultation
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="image-wrapper rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-12 text-center">What Makes Our Support Different</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Industry Specialization</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unlike generic virtual assistants, our professionals are trained specifically for print production environments and understand graphic arts workflows.
                </p>
              </Card>
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Technical Knowledge</h3>
                <p className="text-gray-700 leading-relaxed">
                  Familiarity with Adobe Creative Cloud, file preparation requirements, and print production terminology ensures effective communication.
                </p>
              </Card>
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Workflow Understanding</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our team members understand print production workflows, allowing them to integrate seamlessly into your operational processes.
                </p>
              </Card>
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Dedicated Support</h3>
                <p className="text-gray-700 leading-relaxed">
                  Reliable professionals committed to supporting your business operations with consistency and attention to detail.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-r from-cyan-500 to-green-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Find the Right Support for Your Business</h2>
          <p className="text-xl lg:text-2xl mb-10 opacity-90 max-w-3xl mx-auto">
            Let's discuss your operational needs and identify the best support solution for your print business
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold">
              Schedule a Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
